import type { ModuleKey, Permission } from '@sahab/contracts'
import { allowsAny, moduleEnabled } from '../../app/access'
import { i18n } from '../../lib/i18n'
import type { NavTo } from '../shell/nav'

/**
 * The settings hub's map (pages/settings.md §6, settings-design.md "Layout"): groups the way the
 * business thinks, each with its sections (one form, one resource, one Save) and links to the
 * working pages that hold lists people maintain. The search index lives here too, beside the
 * sections, so a new field is searchable by construction.
 */

export const SECTION_IDS = [
  'appearance',
  'company',
  'typography',
  'printing',
  'numbering',
  'defaults',
  'payment-methods',
  'commissions',
  'tax-rates',
  'stock-policy',
  'account-mapping',
  'lock-date',
  'accounting-checks',
  'tasks',
  'service',
  'service-site-templates',
  'service-asset-types',
  'contracts',
  'projects',
  'project-types',
  'project-cost-categories',
  'project-rate-roles',
  'project-insights',
  'project-contracting',
  'calendar',
  'organization',
  'work-locations',
  'teams',
  'privacy',
  'legal-holds',
  'notifications',
  'ai',
  'modules',
  'regional',
  'system',
] as const
export type SectionId = (typeof SECTION_IDS)[number]

export type GroupId =
  | 'personal'
  | 'company'
  | 'documents'
  | 'salesPurchasing'
  | 'inventory'
  | 'accounting'
  | 'work'
  | 'communication'
  | 'automation'
  | 'administration'

export type LinkId =
  | 'tills'
  | 'warehouses'
  | 'fiscalYears'
  | 'cashBank'
  | 'users'
  | 'roles'
  | 'auditLog'
  | 'workflows'
  | 'workflowRuns'
  | 'delegations'
  | 'messageTemplates'
  | 'messageOutbox'
  | 'loyalty'
  | 'payComponents'
  | 'payrollSettings'
  | 'contributionRules'

/** Why a section is read-only for everyone: changed by the provider, or fixed in this version. */
export type FixedReason = 'provider' | 'numbering' | 'regional' | 'system'

/** One field of a section, as the search finds it: its label key and what people call it. */
export interface SettingFieldDef {
  /** `data-setting-field` on the field's wrapper: where a search result scrolls to. */
  key: string
  /** A label key of the settings namespace (English and Arabic are both searched). */
  label: string
  /** Other words people use for it, in both languages ("VAT", «ضريبة», "invoice number"). */
  keywords?: readonly string[]
}

export interface SectionDef {
  kind: 'section'
  id: SectionId
  to: NavTo
  /**
   * Who sees the section: any one of these. Null: anyone who can open Settings (and, at its
   * address, anyone signed in: tax rates stay readable to all, settings Q7).
   */
  view: readonly Permission[] | null
  /** Who changes it: any one of these. Null: nobody here (read-only, with `fixed` as the reason). */
  edit: readonly Permission[] | null
  fixed?: FixedReason
  /** The company module the section belongs to: without it the section is not offered (D15). */
  module?: ModuleKey
  /**
   * The user's own choices for this browser (Appearance): open to everyone signed in, applied as
   * they are picked, and never the company's settings (no permission, no Save, no audit).
   */
  personal?: boolean
  /** "Last changed … · History": the audit entries of this record. */
  audit?: { entityType: string; entityId?: string }
  /** Words that find the whole section (besides its title). */
  keywords?: readonly string[]
  fields: readonly SettingFieldDef[]
}

export interface LinkDef {
  kind: 'link'
  id: LinkId
  to: NavTo
  permission: Permission
  /** The company module the page belongs to: without it the link is not offered (D15). */
  module?: ModuleKey
  /** Other words that find the link in the hub's search (besides its label). */
  keywords?: readonly string[]
}

const WORKFLOW_KEYWORDS: readonly string[] = [
  'approval',
  'approvals',
  'workflow',
  'maker checker',
  'automation',
  'موافقة',
  'اعتماد',
  'سير العمل',
  'الأتمتة',
]

export interface GroupDef {
  id: GroupId
  items: readonly (SectionDef | LinkDef)[]
}

const MANAGE: readonly Permission[] = ['settings.manage']

export const SETTINGS_GROUPS: readonly GroupDef[] = [
  {
    id: 'personal',
    items: [
      {
        kind: 'section',
        id: 'appearance',
        to: '/settings/appearance',
        view: null,
        edit: null,
        personal: true,
        keywords: [
          'appearance',
          'look',
          'theme',
          'style',
          'dark mode',
          'light mode',
          'colour',
          'color',
          'accent',
          'futuristic',
          'المظهر',
          'السمة',
          'الوضع الداكن',
          'اللون',
        ],
        fields: [
          {
            key: 'appearance.style',
            label: 'appearance.style.title',
            keywords: ['nimbus', 'classic', 'ledger', 'glass', 'كلاسيكي', 'سحابي'],
          },
          {
            key: 'appearance.mode',
            label: 'appearance.mode.title',
            keywords: ['dark', 'light', 'داكن', 'فاتح'],
          },
          {
            key: 'appearance.accent',
            label: 'appearance.accent.title',
            keywords: ['accent', 'colour', 'color', 'brand colour', 'اللون', 'لون التمييز'],
          },
          {
            key: 'appearance.corners',
            label: 'appearance.corners.title',
            keywords: ['corners', 'rounded', 'radius', 'الزوايا', 'مستديرة'],
          },
          {
            key: 'appearance.density',
            label: 'appearance.density.title',
            keywords: ['density', 'compact', 'comfortable', 'row height', 'الكثافة', 'مضغوط'],
          },
          {
            key: 'appearance.ambient',
            label: 'appearance.ambient.title',
            keywords: ['animation', 'motion', 'background', 'الحركة', 'الخلفية'],
          },
        ],
      },
    ],
  },
  {
    id: 'company',
    items: [
      {
        kind: 'section',
        id: 'company',
        to: '/settings/company',
        view: MANAGE,
        edit: MANAGE,
        audit: { entityType: 'CompanyProfile' },
        keywords: ['company', 'business', 'profile', 'الشركة', 'المنشأة'],
        fields: [
          {
            key: 'company.displayName',
            label: 'company.displayName',
            keywords: ['company name', 'trading name', 'name in the app', 'اسم الشركة', 'الاسم التجاري'],
          },
          { key: 'company.displayNameAr', label: 'company.displayNameAr' },
          {
            key: 'company.legalName',
            label: 'company.legalName',
            keywords: ['legal name', 'registered name', 'letterhead', 'الاسم المسجل', 'الترويسة'],
          },
          { key: 'company.legalNameAr', label: 'company.legalNameAr' },
          {
            key: 'company.vatNumber',
            label: 'company.vatNumber',
            keywords: [
              'VAT',
              'VATIN',
              'tax number',
              'TRN',
              'الضريبة',
              'الرقم الضريبي',
              'ضريبة القيمة المضافة',
            ],
          },
          {
            key: 'company.crNumber',
            label: 'company.crNumber',
            keywords: ['CR', 'commercial registration', 'السجل التجاري'],
          },
          { key: 'company.address', label: 'company.address', keywords: ['address', 'location', 'عنوان'] },
          { key: 'company.addressAr', label: 'company.addressAr' },
          {
            key: 'company.phone',
            label: 'company.phone',
            keywords: ['phone', 'telephone', 'mobile', 'هاتف'],
          },
          { key: 'company.email', label: 'company.email', keywords: ['email', 'e-mail', 'بريد'] },
          {
            key: 'company.logo',
            label: 'company.images.logo',
            keywords: ['logo', 'letterhead', 'brand', 'image', 'الشعار', 'شعار', 'صورة'],
          },
          {
            key: 'company.signature',
            label: 'company.images.signature',
            keywords: ['signature', 'sign', 'signed', 'التوقيع', 'توقيع'],
          },
          {
            key: 'company.stamp',
            label: 'company.images.stamp',
            keywords: ['stamp', 'seal', 'rubber stamp', 'الختم', 'ختم'],
          },
        ],
      },
      {
        kind: 'section',
        id: 'typography',
        to: '/settings/typography',
        view: MANAGE,
        edit: MANAGE,
        audit: { entityType: 'SettingGroup', entityId: 'typography' },
        keywords: [
          'font',
          'typeface',
          'text size',
          'font size',
          'typography',
          'الخط',
          'حجم الخط',
          'نوع الخط',
        ],
        fields: [
          {
            key: 'typography.font',
            label: 'typography.font.title',
            keywords: [
              'tajawal',
              'cairo',
              'almarai',
              'noto',
              'readex',
              'plex',
              'تجوال',
              'القاهرة',
              'المراعي',
            ],
          },
          {
            key: 'typography.textSize',
            label: 'typography.textSize.title',
            keywords: ['size', 'bigger', 'larger', 'smaller', 'zoom', 'أكبر', 'أصغر', 'الحجم'],
          },
        ],
      },
    ],
  },
  {
    id: 'documents',
    items: [
      {
        kind: 'section',
        id: 'printing',
        to: '/settings/printing',
        view: MANAGE,
        edit: MANAGE,
        audit: { entityType: 'SettingGroup', entityId: 'documents' },
        keywords: ['print', 'printing', 'printed', 'invoice layout', 'template', 'الطباعة', 'طباعة', 'قالب'],
        fields: [
          {
            key: 'printing.footer',
            label: 'printing.footer.title',
            keywords: ['footer', 'bottom text', 'thank you', 'التذييل', 'تذييل'],
          },
          {
            key: 'printing.termsDefault',
            label: 'printing.terms.default',
            keywords: ['terms', 'conditions', 'T&C', 'terms and conditions', 'الشروط', 'الأحكام'],
          },
          {
            key: 'printing.termsByType',
            label: 'printing.terms.byDocument',
            keywords: [
              'invoice terms',
              'quotation terms',
              'credit note terms',
              'receipt terms',
              'purchase order terms',
              'شروط الفاتورة',
              'شروط عرض السعر',
            ],
          },
          {
            key: 'printing.bankAccounts',
            label: 'printing.payment.accounts',
            keywords: [
              'bank details',
              'bank account',
              'IBAN',
              'account number',
              'payment details',
              'pay to',
              'تفاصيل الدفع',
              'حساب بنكي',
              'الحساب البنكي',
              'آيبان',
              'رقم الحساب',
            ],
          },
          {
            key: 'printing.paymentText',
            label: 'printing.payment.text',
            keywords: ['payment instructions', 'how to pay', 'طريقة الدفع'],
          },
          {
            key: 'printing.qrMode',
            label: 'printing.sharing.qr',
            keywords: ['QR', 'QR code', 'barcode', 'رمز QR', 'رمز الاستجابة'],
          },
          {
            key: 'printing.publicLinkDays',
            label: 'printing.sharing.linkDays',
            keywords: [
              'share link',
              'public link',
              'link expiry',
              'expire',
              'رابط المشاركة',
              'انتهاء الرابط',
            ],
          },
          {
            key: 'printing.showHijri',
            label: 'printing.hijri.label',
            keywords: ['hijri', 'hijri date', 'islamic calendar', 'umm al-qura', 'هجري', 'التاريخ الهجري'],
          },
        ],
      },
      {
        kind: 'section',
        id: 'numbering',
        to: '/settings/numbering',
        view: MANAGE,
        edit: null,
        fixed: 'numbering',
        keywords: [
          'invoice number',
          'document number',
          'numbering',
          'prefix',
          'sequence',
          'next number',
          'series',
          'رقم الفاتورة',
          'ترقيم',
          'تسلسل',
          'بادئة',
          'الرقم التالي',
        ],
        fields: [],
      },
    ],
  },
  {
    id: 'salesPurchasing',
    items: [
      {
        kind: 'section',
        id: 'defaults',
        to: '/settings/defaults',
        view: MANAGE,
        edit: MANAGE,
        audit: { entityType: 'SalesSettings' },
        keywords: ['defaults', 'sales settings', 'purchase settings', 'الافتراضيات', 'إعدادات المبيعات'],
        fields: [
          {
            key: 'defaults.pricesIncludeTax',
            label: 'defaults.pricesIncludeTax',
            keywords: ['VAT inclusive', 'tax inclusive', 'inclusive prices', 'شامل الضريبة', 'شاملة الضريبة'],
          },
          {
            key: 'defaults.paymentTermsDays',
            label: 'defaults.paymentTerms',
            keywords: ['due date', 'payment terms', 'credit days', 'net 30', 'تاريخ الاستحقاق', 'مدة السداد'],
          },
          {
            key: 'defaults.quotationValidDays',
            label: 'defaults.quotationValidity',
            keywords: ['quotation validity', 'valid until', 'quote expiry', 'صلاحية عرض السعر', 'صالح حتى'],
          },
          {
            key: 'defaults.defaultDiscountType',
            label: 'defaults.discountType',
            keywords: ['discount', 'discount type', 'line discount', 'خصم', 'نوع الخصم'],
          },
          // Price levels (ADR-045): the percent off Retail a level sells at without a price of its own.
          {
            key: 'defaults.wholesaleDiscount',
            label: 'priceLevels.wholesale',
            keywords: ['wholesale', 'price level', 'wholesale discount', 'جملة', 'سعر الجملة', 'مستوى السعر'],
          },
          {
            key: 'defaults.distributorDiscount',
            label: 'priceLevels.distributor',
            keywords: ['distributor', 'price level', 'trader price', 'جملة الجملة', 'موزع', 'مستوى السعر'],
          },
        ],
      },
      {
        // Commission (ADR-049): maker-checker and payment through payroll; read by commission readers.
        kind: 'section',
        id: 'commissions',
        to: '/settings/commissions',
        view: ['commissions.read', 'settings.manage'],
        edit: MANAGE,
        module: 'sales',
        audit: { entityType: 'SettingGroup', entityId: 'commissions' },
        keywords: [
          'commission',
          'commissions',
          'incentive',
          'salesperson',
          'technician',
          'العمولات',
          'عمولة',
          'الحوافز',
          'مندوب المبيعات',
        ],
        fields: [
          {
            key: 'commissions.requireSeparateApprover',
            label: 'commissions.makerChecker.label',
            keywords: ['maker checker', 'approver', 'اعتماد', 'الفصل'],
          },
          {
            key: 'commissions.payThroughPayroll',
            label: 'commissions.payroll.label',
            keywords: ['payroll', 'payslip', 'الرواتب', 'قسيمة الراتب'],
          },
        ],
      },
      {
        kind: 'section',
        id: 'payment-methods',
        to: '/settings/payment-methods',
        view: MANAGE,
        edit: MANAGE,
        audit: { entityType: 'SettingGroup', entityId: 'payments' },
        keywords: [
          'payment methods',
          'payment method',
          'cash',
          'card',
          'cheque',
          'check',
          'bank transfer',
          // The accounts themselves, maintained here when Cash & bank is out of reach (ADR-024 D2).
          'bank account',
          'cash and bank accounts',
          'حساب بنكي',
          'حسابات النقد والبنوك',
          'طرق الدفع',
          'طريقة الدفع',
          'نقد',
          'بطاقة',
          'شيك',
          'تحويل بنكي',
        ],
        fields: [
          {
            // The company's own methods (ADR-047).
            key: 'payments.methods',
            label: 'payments.methods.title',
            keywords: [
              'add payment method',
              'Visa',
              'Mastercard',
              'Thawani',
              'Apple Pay',
              'wallet',
              'at the till',
              'إضافة طريقة دفع',
              'فيزا',
              'ماستركارد',
              'ثواني',
              'محفظة',
              'نقطة البيع',
            ],
          },
          {
            key: 'payments.defaultAccounts',
            label: 'payments.defaultAccounts',
            keywords: [
              'default account',
              'deposit account',
              'cash box',
              'bank account',
              'الحساب الافتراضي',
              'الصندوق',
              'الحساب البنكي',
            ],
          },
        ],
      },
      {
        kind: 'section',
        id: 'tax-rates',
        to: '/settings/tax-rates',
        view: null,
        edit: ['taxes.manage'],
        audit: { entityType: 'TaxRate' },
        keywords: [
          'VAT',
          'tax',
          'tax rate',
          '5%',
          'zero rated',
          'exempt',
          'الضريبة',
          'ضريبة',
          'نسبة الضريبة',
          'معفى',
          'صفرية',
        ],
        fields: [],
      },
      { kind: 'link', id: 'tills', to: '/pos/tills', permission: 'pos.manage' },
      {
        // Loyalty points (ADR-044): the programme's terms, tiers and bonus rules live on its own page.
        kind: 'link',
        id: 'loyalty',
        to: '/loyalty/program',
        permission: 'loyalty.read',
        module: 'loyalty',
        keywords: [
          'loyalty',
          'points',
          'rewards',
          'tiers',
          'bonus',
          'redeem',
          'نقاط',
          'الولاء',
          'مكافآت',
          'المستويات',
          'استبدال',
        ],
      },
    ],
  },
  {
    id: 'inventory',
    items: [
      {
        kind: 'section',
        id: 'stock-policy',
        to: '/settings/stock-policy',
        view: MANAGE,
        edit: MANAGE,
        audit: { entityType: 'SettingGroup', entityId: 'inventory' },
        fields: [
          {
            key: 'stock.negativeStock',
            label: 'stock.policy',
            keywords: [
              'negative stock',
              'stock below zero',
              'out of stock',
              'oversell',
              'stock validation',
              'block sales',
              'المخزون السالب',
              'نفاد المخزون',
              'منع البيع',
            ],
          },
          {
            key: 'stock.defaultWarehouse',
            label: 'stock.defaultWarehouse',
            keywords: ['warehouse', 'main warehouse', 'store', 'المستودع', 'المخزن'],
          },
        ],
      },
      { kind: 'link', id: 'warehouses', to: '/warehouses', permission: 'inventory.read' },
    ],
  },
  {
    id: 'accounting',
    items: [
      {
        kind: 'section',
        id: 'account-mapping',
        to: '/settings/account-mapping',
        view: ['ledger.read'],
        edit: ['accounts.manage'],
        audit: { entityType: 'AccountRoles' },
        keywords: [
          'account mapping',
          'GL account',
          'posting accounts',
          'chart of accounts',
          'receivables account',
          'ربط الحسابات',
          'الحسابات',
          'دليل الحسابات',
        ],
        fields: [],
      },
      {
        kind: 'section',
        id: 'lock-date',
        to: '/settings/lock-date',
        view: ['ledger.read'],
        edit: ['fiscalYears.manage'],
        audit: { entityType: 'LockDate' },
        fields: [
          {
            key: 'lock.lockDate',
            label: 'lock.date',
            keywords: [
              'lock date',
              'lock books',
              'close period',
              'closing',
              'period lock',
              'month end',
              'تاريخ الإقفال',
              'إقفال الفترة',
              'قفل الدفاتر',
            ],
          },
          {
            key: 'lock.closedYears',
            label: 'lock.closedYears',
            keywords: ['fiscal year', 'year end', 'closed year', 'السنة المالية', 'إقفال السنة'],
          },
        ],
      },
      // Books health (AA1): which checks run and their thresholds; read with ledger.read.
      {
        kind: 'section',
        id: 'accounting-checks',
        to: '/settings/accounting-checks',
        view: ['ledger.read'],
        edit: MANAGE,
        module: 'accounting',
        audit: { entityType: 'SettingGroup', entityId: 'accountingChecks' },
        keywords: [
          'books health',
          'checks',
          'accounting checks',
          'duplicates',
          'thresholds',
          'month end',
          'سلامة الدفاتر',
          'فحوص',
          'التكرار',
          'الحدود',
          'نهاية الشهر',
          'duplicate bill',
          'duplicate expense',
          'فاتورة مكررة',
          'مصروف مكرر',
        ],
        fields: [],
      },
      { kind: 'link', id: 'fiscalYears', to: '/fiscal-years', permission: 'ledger.read' },
      { kind: 'link', id: 'cashBank', to: '/cash-bank', permission: 'treasury.read' },
    ],
  },
  {
    id: 'work',
    items: [
      {
        kind: 'section',
        id: 'tasks',
        to: '/settings/tasks',
        view: MANAGE,
        edit: MANAGE,
        module: 'tasks',
        audit: { entityType: 'SettingGroup', entityId: 'tasks' },
        keywords: ['tasks', 'task', 'to-do', 'work', 'deadline', 'المهام', 'مهمة', 'العمل', 'موعد التسليم'],
        fields: [
          {
            key: 'tasks.reviewRequiredByDefault',
            label: 'tasks.review.label',
            keywords: ['review', 'reviewer', 'accept', 'approve work', 'مراجعة', 'المراجع', 'قبول'],
          },
          {
            key: 'tasks.assigneeMayChangeDueDate',
            label: 'tasks.dueDate.label',
            keywords: ['due date', 'reschedule', 'deadline', 'تاريخ الاستحقاق', 'تأجيل', 'الموعد'],
          },
          {
            key: 'tasks.escalateOverdueAfterDays',
            label: 'tasks.escalation.label',
            keywords: [
              'overdue',
              'escalation',
              'escalate',
              'team lead',
              'late',
              'متأخرة',
              'تصعيد',
              'قائد الفريق',
            ],
          },
        ],
      },
      {
        kind: 'section',
        id: 'service',
        to: '/settings/service',
        view: MANAGE,
        edit: MANAGE,
        module: 'service',
        audit: { entityType: 'SettingGroup', entityId: 'service' },
        keywords: [
          'service',
          'service jobs',
          'visits',
          'technician',
          'field service',
          'الخدمة',
          'أوامر الخدمة',
          'الزيارات',
          'الفني',
        ],
        fields: [
          {
            key: 'service.priceApproval',
            label: 'service.priceApproval.label',
            keywords: ['price approval', 'estimate', 'quote', 'customer agreement', 'موافقة العميل', 'السعر'],
          },
          {
            key: 'service.reportPhotos',
            label: 'service.reportPhotos.label',
            keywords: ['photos', 'pictures', 'visit report', 'evidence', 'صور', 'تقرير الزيارة'],
          },
          {
            key: 'service.acknowledgment',
            label: 'service.acknowledgment.label',
            keywords: ['acknowledgment', 'signature', 'customer sign', 'sign off', 'إقرار العميل', 'توقيع'],
          },
          {
            key: 'service.assetRegister',
            label: 'service.assetRegister.label',
            keywords: [
              'assets',
              'equipment',
              'sites',
              'places',
              'register',
              'المعدات',
              'المواقع',
              'الأماكن',
              'سجل المعدات',
            ],
          },
          {
            key: 'service.assetChecklists',
            label: 'service.assetChecklists.label',
            keywords: [
              'checklist',
              'asset check',
              'inspection',
              'readings',
              'planned job',
              'قائمة الفحص',
              'فحص المعدات',
              'القراءات',
            ],
          },
        ],
      },
      // Service C1: how sites' places are named, and the company's equipment types.
      {
        kind: 'section',
        id: 'service-site-templates',
        to: '/settings/service-site-templates',
        view: ['service.configure'],
        edit: ['service.configure'],
        module: 'service',
        audit: { entityType: 'ServiceSiteTemplate' },
        keywords: [
          'site templates',
          'levels',
          'floors',
          'apartments',
          'building',
          'tower',
          'قوالب المواقع',
          'المستويات',
          'الطوابق',
          'الشقق',
          'مبنى',
        ],
        fields: [],
      },
      {
        kind: 'section',
        id: 'service-asset-types',
        to: '/settings/service-asset-types',
        view: ['service.configure'],
        edit: ['service.configure'],
        module: 'service',
        audit: { entityType: 'ServiceAssetType' },
        keywords: [
          'equipment types',
          'asset types',
          'AC',
          'air conditioner',
          'checklist',
          'fields',
          'tag prefix',
          'أنواع المعدات',
          'مكيف',
          'قائمة الفحص',
          'الحقول',
        ],
        fields: [],
      },
      // Service C2: maintenance contracts (an opt-in module): reminders, the plan horizon and the defaults.
      {
        kind: 'section',
        id: 'contracts',
        to: '/settings/contracts',
        view: MANAGE,
        edit: MANAGE,
        module: 'contracts',
        audit: { entityType: 'SettingGroup', entityId: 'contracts' },
        keywords: [
          'contract',
          'contracts',
          'maintenance contract',
          'AMC',
          'maintenance',
          'renewal',
          'عقد',
          'عقود',
          'عقد صيانة',
          'صيانة',
          'تجديد',
        ],
        fields: [
          {
            key: 'contracts.renewalNoticeDays',
            label: 'contracts.notice.label',
            keywords: ['renewal', 'reminder', 'notice', 'expiry', 'ends', 'تجديد', 'تذكير', 'انتهاء العقد'],
          },
          {
            key: 'contracts.planAheadDays',
            label: 'contracts.planAhead.title',
            keywords: [
              'planned visits',
              'planned maintenance',
              'preventive maintenance',
              'PPM',
              'horizon',
              'زيارة وقائية',
              'الصيانة الوقائية',
              'خطة الصيانة',
            ],
          },
          {
            key: 'contracts.billingSchedule',
            label: 'contracts.schedule.label',
            keywords: [
              'billing',
              'instalments',
              'installments',
              'monthly',
              'quarterly',
              'yearly',
              'قسط',
              'الأقساط',
              'الفوترة',
            ],
          },
          {
            key: 'contracts.billingTiming',
            label: 'contracts.timing.label',
            keywords: ['in advance', 'in arrears', 'مقدمًا', 'مؤخرًا'],
          },
          {
            key: 'contracts.terms',
            label: 'contracts.terms.title',
            keywords: [
              'coverage',
              'terms',
              'labour',
              'labor',
              'parts',
              'cap',
              'call-outs',
              'response time',
              'التغطية',
              'الشروط',
              'قطع الغيار',
              'بلاغ عطل',
              'مشمول',
              'بمقابل',
            ],
          },
          {
            key: 'contracts.consumableCategoryIds',
            label: 'contracts.consumables.label',
            keywords: ['consumables', 'filters', 'gas', 'categories', 'المستهلكات', 'الفلاتر', 'الفئات'],
          },
        ],
      },
      {
        kind: 'section',
        id: 'projects',
        to: '/settings/projects',
        view: MANAGE,
        edit: MANAGE,
        module: 'projects',
        audit: { entityType: 'SettingGroup', entityId: 'projects' },
        keywords: [
          'projects',
          'project',
          'functions',
          'WBS',
          'plan',
          'المشاريع',
          'مشروع',
          'الوظائف',
          'الخطة',
        ],
        fields: [
          {
            key: 'projects.features',
            label: 'projects.features.title',
            keywords: [
              'functions',
              'switch',
              'plan',
              'timeline',
              'الوظائف',
              'تفعيل',
              'الخطة',
              'المخطط الزمني',
            ],
          },
          {
            key: 'projects.defaultTypeId',
            label: 'projects.defaultType.label',
            keywords: ['default type', 'project type', 'النوع الافتراضي', 'نوع المشروع'],
          },
          {
            key: 'projects.progressMethod',
            label: 'projects.progressMethod.label',
            keywords: ['progress', 'percent complete', 'milestones', 'الإنجاز', 'نسبة الإنجاز', 'المعالم'],
          },
          {
            key: 'projects.budgetThresholds',
            label: 'projects.budgetThresholds.label',
            keywords: ['budget', 'threshold', 'warning', 'over budget', 'الموازنة', 'تجاوز', 'تنبيه'],
          },
          {
            key: 'projects.categoryDefaults',
            label: 'projects.categoryDefaults.label',
            keywords: ['cost category', 'default category', 'فئة التكلفة', 'الفئة الافتراضية'],
          },
          {
            key: 'projects.time.lockAfterDays',
            label: 'projects.time.label',
            keywords: [
              'time',
              'hours',
              'lock',
              'week start',
              'hours per day',
              'الوقت',
              'الساعات',
              'قفل',
              'بداية الأسبوع',
            ],
          },
        ],
      },
      {
        kind: 'section',
        id: 'project-types',
        to: '/settings/project-types',
        view: ['projects.configure'],
        edit: ['projects.configure'],
        module: 'projects',
        audit: { entityType: 'ProjectType' },
        keywords: [
          'project types',
          'template',
          'phases',
          'construction',
          'fit-out',
          'أنواع المشاريع',
          'قالب',
          'المراحل',
          'مقاولات',
        ],
        fields: [],
      },
      {
        kind: 'section',
        id: 'project-cost-categories',
        to: '/settings/project-cost-categories',
        view: ['projects.configure'],
        edit: ['projects.configure'],
        module: 'projects',
        audit: { entityType: 'ProjectCostCategory' },
        keywords: [
          'cost categories',
          'project costs',
          'materials',
          'labour',
          'subcontract',
          'mapping',
          'فئات التكاليف',
          'تكاليف المشاريع',
          'المواد',
          'العمالة',
          'الربط',
        ],
        fields: [],
      },
      {
        kind: 'section',
        id: 'project-rate-roles',
        to: '/settings/project-rate-roles',
        view: ['projects.configure'],
        edit: ['projects.configure'],
        module: 'projects',
        audit: { entityType: 'ProjectRole' },
        keywords: [
          'rate roles',
          'bill rate',
          'hourly rate',
          'time and materials',
          'أدوار التسعير',
          'سعر الساعة',
          'الوقت والمواد',
        ],
        fields: [],
      },
      {
        kind: 'section',
        id: 'project-insights',
        to: '/settings/project-insights',
        view: MANAGE,
        edit: MANAGE,
        module: 'projects',
        audit: { entityType: 'SettingGroup', entityId: 'projectInsights' },
        keywords: [
          'insights',
          'health',
          'signals',
          'alerts',
          'thresholds',
          'forecast',
          'earned value',
          'المؤشرات',
          'حالة المشروع',
          'التنبيهات',
          'الحدود',
          'التوقعات',
        ],
        fields: [
          {
            key: 'projectInsights.burnGapPts',
            label: 'projectInsights.burnGapPts.label',
            keywords: ['spend ahead of progress', 'burn', 'الصرف قبل الإنجاز'],
          },
          {
            key: 'projectInsights.eacMethod',
            label: 'projectInsights.eacMethod.label',
            keywords: ['EAC', 'final cost', 'estimate at completion', 'التكلفة النهائية'],
          },
          {
            key: 'projectInsights.slipToleranceDays',
            label: 'projectInsights.slipToleranceDays.label',
            keywords: ['schedule slip', 'late finish', 'تأخر موعد الانتهاء'],
          },
          {
            key: 'projectInsights.arOverdueDays',
            label: 'projectInsights.arOverdueDays.label',
            keywords: ['overdue', 'receivable', 'late payment', 'تأخر السداد'],
          },
          {
            key: 'projectInsights.staleDays',
            label: 'projectInsights.staleDays.label',
            keywords: ['stale', 'no activity', 'inactive', 'لا نشاط'],
          },
        ],
      },
      {
        kind: 'section',
        id: 'project-contracting',
        to: '/settings/project-contracting',
        view: MANAGE,
        edit: MANAGE,
        module: 'projects',
        audit: { entityType: 'SettingGroup', entityId: 'projects' },
        keywords: [
          'contracting',
          'contract',
          'construction',
          'progress claims',
          'bill of quantities',
          'BOQ',
          'المقاولات',
          'العقد',
          'المستخلصات',
          'جدول الكميات',
        ],
        fields: [
          {
            key: 'projectContracting.measure',
            label: 'projectContracting.measure.label',
            keywords: ['measure', 'over-measure', 'quantity', 'percentage', 'القياس', 'الكمية', 'النسبة'],
          },
          {
            key: 'projectContracting.payment',
            label: 'projectContracting.payment.label',
            keywords: ['payment days', 'certificate due', 'أيام السداد', 'استحقاق المستخلص'],
          },
          {
            key: 'projectContracting.retention',
            label: 'projectContracting.retention.label',
            keywords: [
              'retention',
              'cap',
              'release',
              'defects period',
              'DLP',
              'المحتجزات',
              'فترة ضمان العيوب',
            ],
          },
          {
            key: 'projectContracting.advance',
            label: 'projectContracting.advance.label',
            keywords: ['advance payment', 'recovery', 'pro rata', 'FIDIC', 'الدفعة المقدمة', 'الاسترداد'],
          },
          {
            key: 'projectContracting.damages',
            label: 'projectContracting.damages.label',
            keywords: ['liquidated damages', 'delay', 'penalty', 'غرامات التأخير'],
          },
          {
            key: 'projectContracting.approval',
            label: 'projectContracting.approval.label',
            keywords: ['internal approval', 'four eyes', 'workflow', 'الاعتماد الداخلي', 'مسار الاعتماد'],
          },
          {
            key: 'projectContracting.guarantees',
            label: 'projectContracting.guarantees.label',
            keywords: [
              'guarantee',
              'bond',
              'reminder',
              'expiry',
              'performance bond',
              'الضمانات',
              'ضمان حسن التنفيذ',
            ],
          },
        ],
      },
      {
        kind: 'section',
        id: 'calendar',
        to: '/settings/calendar',
        view: null,
        edit: MANAGE,
        audit: { entityType: 'SettingGroup', entityId: 'calendar' },
        keywords: [
          'calendar',
          'working calendar',
          'working days',
          'working hours',
          'weekend',
          'holiday',
          'holidays',
          'public holiday',
          'Eid',
          'التقويم',
          'أيام العمل',
          'ساعات العمل',
          'عطلة',
          'العطل الرسمية',
          'عطلة نهاية الأسبوع',
          'العيد',
        ],
        fields: [
          {
            key: 'calendar.week',
            label: 'calendar.week.title',
            keywords: ['work week', 'weekend', 'Friday', 'hours', 'أسبوع العمل', 'الجمعة', 'الدوام'],
          },
          {
            key: 'calendar.holidays',
            label: 'calendar.holidays.title',
            keywords: [
              'public holiday',
              'Eid',
              'National Day',
              'import',
              'عطلة رسمية',
              'العيد',
              'العيد الوطني',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'communication',
    items: [
      {
        kind: 'section',
        id: 'notifications',
        to: '/settings/notifications',
        view: ['settings.manage', 'settings.manage_secrets'],
        edit: MANAGE,
        audit: { entityType: 'SettingGroup', entityId: 'notifications' },
        keywords: [
          'notifications',
          'messages',
          'messaging',
          'e-mail',
          'email',
          'mail',
          'الإشعارات',
          'الرسائل',
          'المراسلة',
          'بريد',
          'البريد الإلكتروني',
        ],
        fields: [
          {
            key: 'notifications.fromName',
            label: 'notifications.fromName',
            keywords: ['email sender', 'sender name', 'from name', 'اسم المرسل', 'البريد الإلكتروني'],
          },
          {
            key: 'notifications.replyTo',
            label: 'notifications.replyTo',
            keywords: ['reply-to', 'reply address', 'الرد إلى'],
          },
          {
            key: 'notifications.emailDelivery',
            label: 'notifications.delivery.title',
            keywords: ['SMTP', 'mail server', 'relay', 'delivery', 'خادم البريد', 'إرسال البريد'],
          },
          {
            key: 'notifications.smtpPassword',
            label: 'notifications.password.label',
            keywords: ['SMTP password', 'secret', 'كلمة المرور', 'كلمة مرور البريد'],
          },
          {
            key: 'notifications.testEmail',
            label: 'notifications.test.title',
            keywords: ['test e-mail', 'test email', 'send test', 'رسالة تجريبية', 'تجربة الإرسال'],
          },
        ],
      },
      {
        kind: 'link',
        id: 'messageTemplates',
        to: '/settings/message-templates',
        permission: 'messaging.manage',
        keywords: [
          'template',
          'templates',
          'e-mail text',
          'email text',
          'wording',
          'قالب',
          'القوالب',
          'نص الرسالة',
        ],
      },
      {
        kind: 'link',
        id: 'messageOutbox',
        to: '/settings/message-outbox',
        permission: 'messaging.manage',
        keywords: [
          'outbox',
          'sent e-mails',
          'sent emails',
          'failed e-mails',
          'delivery log',
          'صندوق الصادر',
          'الرسائل المرسلة',
        ],
      },
    ],
  },
  {
    id: 'automation',
    items: [
      {
        // The AI assistant (P8, 05 §9.4): the company's OpenAI key, model, limits, features, retention
        // and residency approval; its Usage and runs page sits under it (`/settings/ai/usage`).
        kind: 'section',
        id: 'ai',
        to: '/settings/ai',
        view: ['ai.manage'],
        edit: ['ai.manage'],
        module: 'ai',
        keywords: [
          'AI',
          'assistant',
          'artificial intelligence',
          'OpenAI',
          'ChatGPT',
          'GPT',
          'tokens',
          'usage',
          'المساعد الذكي',
          'الذكاء الاصطناعي',
          'المساعد',
          'الرموز',
        ],
        fields: [
          {
            key: 'ai.enabled',
            label: 'ai.enabled.label',
            keywords: ['turn on', 'enable', 'acknowledgement', 'تفعيل', 'الإقرار'],
          },
          {
            key: 'ai.key',
            label: 'ai.key.title',
            keywords: [
              'API key',
              'OpenAI key',
              'secret',
              'key check',
              'مفتاح',
              'مفتاح OpenAI',
              'فحص المفتاح',
            ],
          },
          { key: 'ai.model', label: 'ai.model.label', keywords: ['model', 'GPT', 'النموذج'] },
          {
            key: 'ai.limits',
            label: 'ai.limits.title',
            keywords: ['limit', 'budget', 'tokens', 'runs per day', 'cap', 'الحد', 'الرموز', 'الميزانية'],
          },
          {
            key: 'ai.features',
            label: 'ai.features.title',
            keywords: ['features', 'status draft', 'plan', 'BOQ', 'الميزات', 'مسودة', 'الخطة'],
          },
          {
            key: 'ai.retention',
            label: 'ai.retention.title',
            keywords: ['retention', 'delete', 'purge', 'conversations', 'الاحتفاظ', 'حذف', 'المحادثات'],
          },
          {
            key: 'ai.residency',
            label: 'ai.residency.title',
            keywords: [
              'residency',
              'Oman',
              'outside Oman',
              'data location',
              'إقامة البيانات',
              'عُمان',
              'خارج عُمان',
            ],
          },
        ],
      },
      {
        kind: 'link',
        id: 'workflows',
        to: '/workflows',
        permission: 'workflows.read',
        keywords: WORKFLOW_KEYWORDS,
      },
      {
        kind: 'link',
        id: 'workflowRuns',
        to: '/workflows/runs',
        permission: 'workflows.runs',
        keywords: WORKFLOW_KEYWORDS,
      },
      {
        kind: 'link',
        id: 'delegations',
        to: '/workflows/delegations',
        permission: 'workflows.delegations',
        keywords: [...WORKFLOW_KEYWORDS, 'delegation', 'out of office', 'تفويض', 'إنابة'],
      },
    ],
  },
  {
    id: 'administration',
    items: [
      { kind: 'link', id: 'users', to: '/users', permission: 'users.read' },
      { kind: 'link', id: 'roles', to: '/roles', permission: 'roles.read' },
      {
        // The organisation tree (ADR-025): units, their heads and the HR business partner.
        kind: 'section',
        id: 'organization',
        to: '/settings/organization',
        view: ['orgUnits.manage'],
        edit: ['orgUnits.manage'],
        audit: { entityType: 'OrgUnit' },
        keywords: [
          'organisation',
          'organization',
          'org chart',
          'org units',
          'department',
          'section',
          'directorate',
          'head',
          'acting head',
          'HRBP',
          'الهيكل التنظيمي',
          'الوحدات التنظيمية',
          'دائرة',
          'قسم',
          'مديرية',
          'الرئيس',
          'بالإنابة',
        ],
        fields: [],
      },
      {
        // Where people work (ADR-025): offices and sites by governorate and wilayat.
        kind: 'section',
        id: 'work-locations',
        to: '/settings/work-locations',
        view: ['orgUnits.manage'],
        edit: ['orgUnits.manage'],
        audit: { entityType: 'WorkLocation' },
        keywords: [
          'work locations',
          'office',
          'site',
          'branch office',
          'governorate',
          'wilayat',
          'مواقع العمل',
          'مكتب',
          'موقع',
          'المحافظة',
          'الولاية',
        ],
        fields: [],
      },
      {
        // The company's teams (ADR-023), whatever modules it has: Tasks and Service both use them.
        kind: 'section',
        id: 'teams',
        to: '/settings/teams',
        view: ['teams.manage'],
        edit: ['teams.manage'],
        audit: { entityType: 'TaskTeam' },
        keywords: [
          'teams',
          'team',
          'crew',
          'members',
          'team lead',
          'technicians',
          'الفرق',
          'فريق',
          'الأعضاء',
          'قائد الفريق',
          'الفنيون',
        ],
        fields: [],
      },
      // Human resources and payroll (ADR-048): their set-up lives on their own pages.
      {
        kind: 'link',
        id: 'payComponents',
        to: '/hr/pay-components',
        permission: 'payComponents.manage',
        module: 'hr',
        keywords: [
          'salary structure',
          'pay components',
          'allowances',
          'basic salary',
          'housing allowance',
          'هيكل الرواتب',
          'عناصر الراتب',
          'البدلات',
          'الراتب الأساسي',
        ],
      },
      {
        kind: 'link',
        id: 'contributionRules',
        to: '/payroll/contributions',
        permission: 'payroll.settings',
        module: 'payroll',
        keywords: [
          'social protection',
          'SPF',
          'social insurance',
          'contributions',
          'PASI',
          'الحماية الاجتماعية',
          'التأمينات الاجتماعية',
          'الاشتراكات',
        ],
      },
      {
        kind: 'link',
        id: 'payrollSettings',
        to: '/payroll/settings',
        permission: 'payroll.settings',
        module: 'payroll',
        keywords: [
          'payroll',
          'WPS',
          'wage protection',
          'overtime',
          'gratuity',
          'end of service',
          'sick leave',
          'الرواتب',
          'حماية الأجور',
          'العمل الإضافي',
          'مكافأة نهاية الخدمة',
        ],
      },
      {
        // Data retention (R0b W7, ADR-025): how long personal data is kept, per class, within floors.
        kind: 'section',
        id: 'privacy',
        to: '/settings/privacy',
        view: ['privacy.manage'],
        edit: ['privacy.manage'],
        audit: { entityType: 'RetentionRule' },
        keywords: [
          'privacy',
          'personal data',
          'data protection',
          'retention',
          'anonymise',
          'anonymize',
          'GDPR',
          'PDPL',
          'الخصوصية',
          'البيانات الشخصية',
          'حماية البيانات',
          'الاحتفاظ',
          'إخفاء الهوية',
        ],
        fields: [
          {
            key: 'privacy.rules',
            label: 'privacyRules.rulesTitle',
            keywords: ['retention period', 'months', 'مدة الاحتفاظ', 'أشهر'],
          },
          {
            key: 'privacy.runs',
            label: 'privacyRules.runsTitle',
            keywords: ['retention run', 'last run', 'آخر تشغيل'],
          },
        ],
      },
      {
        // Legal holds (R0b W7): a held record is never anonymised.
        kind: 'section',
        id: 'legal-holds',
        to: '/settings/legal-holds',
        view: ['privacy.manage'],
        edit: ['privacy.manage'],
        audit: { entityType: 'LegalHold' },
        keywords: [
          'legal hold',
          'litigation hold',
          'dispute',
          'keep',
          'freeze',
          'الحجز القانوني',
          'نزاع',
          'تجميد',
        ],
        fields: [],
      },
      {
        kind: 'section',
        id: 'modules',
        to: '/settings/modules',
        view: null,
        edit: null,
        fixed: 'provider',
        keywords: ['modules', 'features', 'enabled', 'subscription', 'الوحدات', 'الاشتراك'],
        fields: [],
      },
      {
        kind: 'section',
        id: 'regional',
        to: '/settings/regional',
        view: null,
        edit: null,
        fixed: 'regional',
        keywords: [
          'date format',
          'currency',
          'time zone',
          'decimals',
          'digits',
          'numbers',
          'تنسيق التاريخ',
          'العملة',
          'المنطقة الزمنية',
          'الأرقام',
          'hijri',
          'هجري',
        ],
        fields: [],
      },
      {
        kind: 'section',
        id: 'system',
        to: '/settings/system',
        view: null,
        edit: null,
        fixed: 'system',
        keywords: ['version', 'build', 'environment', 'support', 'about', 'الإصدار', 'الدعم', 'حول'],
        fields: [],
      },
      { kind: 'link', id: 'auditLog', to: '/audit-log', permission: 'audit.read' },
    ],
  },
]

export const SECTIONS: readonly SectionDef[] = SETTINGS_GROUPS.flatMap((g) =>
  g.items.filter((i): i is SectionDef => i.kind === 'section'),
)

export function sectionDef(id: SectionId): SectionDef {
  const def = SECTIONS.find((s) => s.id === id)
  if (!def) throw new Error(`Unknown settings section: ${id}`)
  return def
}

export function groupOf(id: SectionId): GroupId {
  return SETTINGS_GROUPS.find((g) => g.items.some((i) => i.kind === 'section' && i.id === id))?.id as GroupId
}

/**
 * Who sees Settings at all (the sidebar entry, `/settings`): whoever holds a permission some section
 * is for. Tax rates, Modules, Regional and System information are then open to them too.
 */
export const HUB_PERMISSIONS: readonly Permission[] = [
  ...new Set(SECTIONS.flatMap((s) => [...(s.view ?? []), ...(s.edit ?? [])])),
]

type Granted = ReadonlySet<Permission> | readonly Permission[]
/** The company's modules (the session's `modules`; undefined: every module, as `moduleEnabled`). */
type Modules = readonly ModuleKey[] | undefined

export function canOpenHub(granted: Granted): boolean {
  return allowsAny(granted, HUB_PERMISSIONS)
}

/** Whether the company has the section's module (sections of no module: always). */
function inModules(section: SectionDef, modules: Modules): boolean {
  return section.module === undefined || moduleEnabled(modules, section.module)
}

/** Whether the rail lists the section. */
export function canSeeSection(granted: Granted, section: SectionDef, modules?: Modules): boolean {
  if (section.personal) return true
  if (!inModules(section, modules)) return false
  return section.view === null ? canOpenHub(granted) : allowsAny(granted, section.view)
}

/** Whether the section's address opens (a section for everyone opens even outside the hub). */
export function canOpenSection(granted: Granted, section: SectionDef, modules?: Modules): boolean {
  if (!inModules(section, modules)) return false
  return section.view === null || allowsAny(granted, section.view)
}

export function canEditSection(granted: Granted, section: SectionDef): boolean {
  return section.edit !== null && allowsAny(granted, section.edit)
}

/** The groups and items the user may see, empty groups dropped. */
export function visibleGroups(granted: Granted, modules?: Modules): GroupDef[] {
  const has = (p: Permission) => ('has' in granted ? granted.has(p) : granted.includes(p))
  return SETTINGS_GROUPS.flatMap((group) => {
    const items = group.items.filter((item) =>
      item.kind === 'section'
        ? canSeeSection(granted, item, modules)
        : has(item.permission) && (item.module === undefined || moduleEnabled(modules, item.module)),
    )
    return items.length > 0 ? [{ ...group, items }] : []
  })
}

/**
 * Where `/settings` opens: the first section the user may change, else the first they may see (a
 * reader of the ledger opens on a read-only section), or null.
 */
export function firstSection(granted: Granted, modules?: Modules): SectionDef | null {
  const sections = visibleGroups(granted, modules).flatMap((g) =>
    g.items.filter((i): i is SectionDef => i.kind === 'section'),
  )
  // The user's own Appearance opens only when there is nothing of the company's to show.
  const company = sections.filter((s) => !s.personal)
  return company.find((s) => canEditSection(granted, s)) ?? company[0] ?? sections[0] ?? null
}

// ── search ──────────────────────────────────────────────────────────────────

/**
 * For matching: lower case, Arabic letter variants folded (أ إ آ → ا, ة → ه, ى → ي, ؤ → و, ئ → ي),
 * tatweel and harakat dropped, punctuation to spaces, runs of spaces to one.
 */
export function normalizeSearch(text: string): string {
  return text
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/[ـً-ْ]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}%&]+/gu, ' ')
    .trim()
}

/** Every word of the query appears in the text (as the start of one of its words, or inside it). */
function matchesText(text: string, words: readonly string[]): boolean {
  const hay = ` ${text} `
  return words.every((w) => hay.includes(w))
}

type Labeller = (key: string) => string

function labellers(): Labeller[] {
  return (['en', 'ar'] as const).map((lng) => {
    const t = i18n.getFixedT(lng, 'settings') as unknown as (key: string) => string
    return (key: string) => t(key)
  })
}

export interface SectionMatch {
  section: SectionDef
  /** The section itself matched (its title or words), not only some of its fields. */
  whole: boolean
  /** The fields that matched, in the section's order. */
  fields: SettingFieldDef[]
}

/**
 * The sections (and fields) a query finds, in the rail's order, among those the user may see.
 * English and Arabic labels are both searched, with each field's keywords (intent phrases such as
 * "invoice number" or «رقم الفاتورة»).
 */
export function searchSettings(query: string, sections: readonly SectionDef[]): SectionMatch[] {
  const words = normalizeSearch(query).split(' ').filter(Boolean)
  if (words.length === 0) return []
  const labels = labellers()
  const text = (parts: readonly string[]) => normalizeSearch(parts.join(' '))
  return sections.flatMap((section) => {
    const title = labels.map((l) => l(`sections.${section.id}.title`))
    const whole = matchesText(text([...title, ...(section.keywords ?? [])]), words)
    const fields = section.fields.filter((field) =>
      matchesText(text([...labels.map((l) => l(field.label)), ...(field.keywords ?? [])]), words),
    )
    return whole || fields.length > 0 ? [{ section, whole, fields }] : []
  })
}
